/**
 * Types for field extraction rules configuration
 */

// Single condition for extraction rule evaluation
export interface ExtractionCondition {
  field: string; // e.g., 'building.clusters', 'building.googlePlaceLongAddress'
  operator: '==' | '!=' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with';
  value: string;
}

// Condition group (AND/OR logic) for complex rules
export interface ExtractionConditionGroup {
  logic: 'AND' | 'OR';
  conditions: (ExtractionCondition | ExtractionConditionGroup)[];
}

// Calculation rule for deriving field values
export interface CalculationRule {
  baseField: 'documentDate' | 'now'; // What to calculate from
  operation: 'add' | 'subtract';
  amount: number;
  unit: 'days' | 'months' | 'years';
  conditions?: ExtractionConditionGroup; // When to apply this rule
}

// AI fallback configuration when no calculation rule matches
export interface AIFallbackConfig {
  enabled: boolean;
  customPrompt?: string; // e.g., "Wat is de geldigheidsduur van {label} in {address}?"
  // Available placeholders: {label}, {address}, {building.clusters}, {documentDate}
}

// Per-field extraction rule configuration
export interface FieldExtractionRule {
  id?: string; // Unique identifier for the rule
  name?: string; // Human-readable name for the rule
  priority?: number; // Order of evaluation (lower = first)
  calculationRule?: CalculationRule; // Calculation to apply
  aiFallback?: AIFallbackConfig; // AI fallback if calculation doesn't apply
  searchHint?: string; // Direct extraction hint
  negativeClause?: string; // What to avoid
}

// Extended standard field extraction with rules
export interface StandardFieldExtraction {
  enabled: boolean;
  searchHint?: string;
  negativeClause?: string;
  extractionRules?: FieldExtractionRule[];
  aiFallback?: AIFallbackConfig;
}

// Available building fields for condition evaluation
export interface BuildingFieldOption {
  key: string;
  label: string;
  example: string;
  type: 'string' | 'number' | 'boolean' | 'array';
}

// Building fields available for conditions
export const BUILDING_CONDITION_FIELDS: BuildingFieldOption[] = [
  { key: 'building.cluster.name', label: 'Cluster (regio)', example: 'Vlaanderen', type: 'string' },
  { key: 'building.clusters', label: 'Clusters (array)', example: 'Vlaanderen, Wallonië', type: 'array' },
  { key: 'building.googlePlaceLongAddress', label: 'Gebouwadres', example: 'Kerkstraat 1, 2500 Lier', type: 'string' },
  { key: 'building.buildingType', label: 'Gebouwtype', example: 'Appartementsgebouw', type: 'string' },
  { key: 'building.constructionYear', label: 'Bouwjaar', example: '1985', type: 'number' },
  { key: 'building.postalCode', label: 'Postcode', example: '2500', type: 'string' },
  { key: 'building.address', label: 'Adres (kort)', example: 'Kerkstraat 1', type: 'string' },
];

// Operator labels for UI
export const OPERATOR_LABELS: Record<ExtractionCondition['operator'], string> = {
  '==': 'is gelijk aan',
  '!=': 'is niet gelijk aan',
  'contains': 'bevat',
  'not_contains': 'bevat niet',
  'starts_with': 'begint met',
  'ends_with': 'eindigt met',
};

// Time unit labels for UI
export const TIME_UNIT_LABELS: Record<CalculationRule['unit'], string> = {
  days: 'dagen',
  months: 'maanden',
  years: 'jaren',
};

// AI prompt placeholders
export const AI_PROMPT_PLACEHOLDERS = [
  { key: '{label}', description: 'De naam van het document label' },
  { key: '{address}', description: 'Het volledige adres van het gebouw' },
  { key: '{building.clusters}', description: 'De clusters/regio\'s van het gebouw' },
  { key: '{documentDate}', description: 'De geëxtraheerde documentdatum' },
];
